<!--
  Support.svelte — the customer's support home: which products they can get
  help with, and their tickets.

  Eligibility is per product and read live from each product's licensing
  backend, so the page opens with one card per support-enabled product saying
  plainly whether it is available — and, when it is not, why and where to get a
  licence. The Support nav row is shown to everyone for that reason: a customer
  who cannot file a ticket still needs to learn why here, not by the row being
  missing.

  Below the cards, every ticket the user can read, open and closed alike, with
  no filters: a customer has a handful of tickets, not a queue, and a filter
  defaulting to "open" hid the very ticket someone came back to look up. Tickets
  that are not their own — filed by a seat holder against a licence they own,
  or shared with them — say so on the row instead.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Button,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import {
		EmptyState,
		LicenseAppIcon,
		PageHeader,
		RelativeTime,
		RoutedButton,
		RoutedLink,
		SupportCommunityHelp,
		SupportPriorityBadge,
		SupportStatusBadge
	} from '../../components';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import { getSupportOverview, listSupportTickets } from '../../AppNetwork';
	import type { SupportOverview, SupportProduct, SupportTicketSummary } from '../../AppTypes';
	import { supportReasonText } from '../../support/supportFormat';
	import { t, t2 } from '../../i18n';

	/** Page size of the ticket list. */
	const limit = 20;

	/** The overview: agent flag, products, open count. */
	let overview = $state<SupportOverview | null>(null);
	/** Whether the overview is loading. */
	let loadingOverview = $state(true);
	/** Failure of the overview. */
	let overviewError = $state('');

	/** The current page of tickets. */
	let tickets: SupportTicketSummary[] = $state([]);
	/** How many tickets match in all. */
	let total = $state(0);
	/** Whether the ticket list is loading. */
	let loadingTickets = $state(true);
	/** Failure of the ticket list. */
	let ticketsError = $state('');
	/** Current page, from zero. */
	let page = $state(0);

	/** Loads the overview. */
	async function loadOverview(): Promise<void> {
		loadingOverview = true;
		overviewError = '';
		/** The overview carries every product's live eligibility. */
		try {
			overview = await getSupportOverview();
		} catch (e) {
			overviewError = e instanceof Error ? e.message : t2('Failed to load support');
		} finally {
			loadingOverview = false;
		}
	}

	/** Loads the current page of tickets. */
	async function loadTickets(): Promise<void> {
		loadingTickets = true;
		ticketsError = '';
		/** Everything the user can read, open and closed, paged server-side. */
		try {
			const result = await listSupportTickets({ scope: 'all', state: 'all', limit, offset: page * limit });
			tickets = result.items;
			total = result.total;
		} catch (e) {
			tickets = [];
			ticketsError = e instanceof Error ? e.message : t2('Failed to load tickets');
		} finally {
			loadingTickets = false;
		}
	}

	/** Whether the product will take a ticket from this user right now. */
	function isAvailable(product: SupportProduct): boolean {
		return product.eligibility?.isSupportAvailable === true;
	}

	/** Products the user may file against — what "New ticket" in the header needs at least one of. */
	let availableProducts = $derived((overview?.products ?? []).filter(isAvailable));

	onMount(() => {
		loadOverview();
		loadTickets();
	});
</script>

<PageHeader
	title={$t('Support')}
	description={$t('Get help with the Abstract products you hold a license for.')}
	breadcrumbs={[{ label: $t('Support') }]}
/>

<div class="space-y-6">
	{#if loadingOverview}
		<EmptyState variant="loading" />
	{:else if overviewError}
		<EmptyState variant="error" description={overviewError}>
			<Button size="sm" onclick={loadOverview}>{$t('Retry')}</Button>
		</EmptyState>
	{:else if overview && overview.isEnabled === false}
		<!-- Switched off platform-wide: say so, and still point at the community. -->
		<EmptyState
			faIcon="life-ring"
			title={$t('Support is currently turned off')}
			description={$t('Tickets cannot be opened or answered right now. Please check back later.')}
		/>
		<SupportCommunityHelp links={overview.communityLinks} />
	{:else if overview && overview.products.length === 0}
		<EmptyState
			faIcon="life-ring"
			title={$t('Support is not available yet')}
			description={$t('None of the Abstract products offer support through Abstract ID yet.')}
		/>
		<SupportCommunityHelp links={overview.communityLinks} />
	{:else if overview}
		<section aria-label={$t('Products')} class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
			{#each overview.products as product (product.applicationUUID)}
				<AbstractCard class="max-w-none gap-3" data-product={product.applicationUUID}>
					<div class="flex items-center gap-3">
						<div class="bg-primary-100 dark:bg-primary-900/30 flex h-10 w-10 items-center justify-center rounded-lg">
							<LicenseAppIcon
								iconImageName={product.iconImageName}
								icon={product.icon}
								name={product.name}
								class="h-6 w-6"
							/>
						</div>
						<div class="min-w-0">
							<p class="font-semibold text-gray-900 dark:text-white">{product.name}</p>
							{#if isAvailable(product)}
								<p class="text-sm text-green-700 dark:text-green-400">
									<i class="fa-solid fa-circle-check me-1"></i>{$t('Support available')}
								</p>
							{:else if product.error || !product.eligibility}
								<p class="text-sm text-amber-700 dark:text-amber-400">
									<i class="fa-solid fa-circle-exclamation me-1"></i>{$t('Could not check your license')}
								</p>
							{:else}
								<p class="text-default text-sm">
									<i class="fa-solid fa-circle-minus me-1"></i>{$t('Support not available')}
								</p>
							{/if}
						</div>
					</div>

					{#if isAvailable(product)}
						<RoutedButton size="sm" href="/Support/New/{product.applicationUUID}" class="self-start">
							<i class="fa-solid fa-plus me-2"></i>{$t('New ticket')}
						</RoutedButton>
					{:else if product.error || !product.eligibility}
						<p class="text-default text-sm">
							{$t('The license server for this product did not answer. Please try again shortly.')}
						</p>
					{:else}
						<p class="text-default text-sm">{$t(supportReasonText(product.eligibility.reason))}</p>
						<!-- Offered only where the product says where to go; inventing a destination
						     would be worse than making no offer. -->
						{#if product.licensePurchaseURL && product.eligibility.reason !== 'not-configured'}
							<a
								href={product.licensePurchaseURL}
								target="_blank"
								rel="noopener noreferrer"
								class="text-primary-600 dark:text-primary-400 self-start text-sm font-medium hover:underline"
							>
								<i class="fa-solid fa-cart-shopping me-1"></i>{$t('Get a license for {{name}}', {
									name: product.name
								})}
							</a>
						{/if}
					{/if}
				</AbstractCard>
			{/each}
		</section>
		<!-- Offered whenever some product is out of reach: a refusal needs a way out. -->
		{#if overview.products.some((product) => !isAvailable(product))}
			<SupportCommunityHelp links={overview.communityLinks} />
		{/if}
	{/if}

	<!-- Hidden while support is off: the list would only answer that it is. It is
	     loaded alongside the overview rather than after it, so the page is not held
	     up by a switch that is almost always on. -->
	{#if overview?.isEnabled !== false}
	<section aria-label={$t('Tickets')} class="space-y-3">
		<h2 class="text-lg font-semibold text-gray-900 dark:text-white">{$t('Your tickets')}</h2>

		{#if loadingTickets}
			<EmptyState variant="loading" compact />
		{:else if ticketsError}
			<EmptyState variant="error" description={ticketsError} />
		{:else if tickets.length === 0}
			<!-- Outside the table: an empty state in a colspan row leaves the browser
			     sizing columns from the header band alone. -->
			<EmptyState
				faIcon="life-ring"
				title={$t('No tickets yet')}
				description={availableProducts.length > 0
					? $t('When something is not working, open a ticket and the support team will get back to you.')
					: ''}
			/>
		{:else}
			<Table divClass="widget !p-1 relative" class="dataTable">
				<TableHead>
					<TableHeadCell class="w-28">{$t('Ticket')}</TableHeadCell>
					<TableHeadCell>{$t('Subject')}</TableHeadCell>
					<TableHeadCell class="w-40">{$t('Status')}</TableHeadCell>
					<TableHeadCell class="w-28">{$t('Priority')}</TableHeadCell>
					<TableHeadCell class="w-36">{$t('Last activity')}</TableHeadCell>
				</TableHead>
				<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
					{#each tickets as ticket (ticket.ticketUUID)}
						<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
							<TableBodyCell class="font-mono text-xs !whitespace-nowrap">
								<RoutedLink to="/Support/Tickets/{ticket.ticketKey}">{ticket.ticketKey}</RoutedLink>
							</TableBodyCell>
							<TableBodyCell>
								<RoutedLink to="/Support/Tickets/{ticket.ticketKey}">
									<span class="font-medium text-gray-900 dark:text-white">{ticket.subject}</span>
								</RoutedLink>
								<p class="text-default text-xs">
									{ticket.application.name}{#if ticket.category}
										· {ticket.category.name}{/if}
									{#if ticket.viewerRelation === 'participant'}
										· <i class="fa-solid fa-user-plus me-0.5"></i>{$t('Shared by {{name}}', {
											name: ticket.requester.name
										})}
									{:else if !ticket.isOwnTicket}
										· <i class="fa-solid fa-user-group me-0.5"></i>{$t('Filed by {{name}}', {
											name: ticket.requester.name
										})}
									{/if}
								</p>
							</TableBodyCell>
							<TableBodyCell><SupportStatusBadge status={ticket.status} audience="customer" /></TableBodyCell>
							<TableBodyCell><SupportPriorityBadge priority={ticket.priority} /></TableBodyCell>
							<TableBodyCell class="text-default text-sm"><RelativeTime date={ticket.lastActivityAt} /></TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>

			{#if total > limit}
				<div class="flex items-center justify-between">
					<p class="text-default text-sm">
						{$t('Showing {{from}} to {{to}} of {{total}}', {
							from: page * limit + 1,
							to: Math.min((page + 1) * limit, total),
							total
						})}
					</p>
					<div class="flex gap-2">
						<Button
							size="sm"
							color="alternative"
							disabled={page === 0}
							onclick={() => {
								page--;
								loadTickets();
							}}><i class="fa-solid fa-chevron-left me-1"></i>{$t('Previous')}</Button
						>
						<Button
							size="sm"
							color="alternative"
							disabled={(page + 1) * limit >= total}
							onclick={() => {
								page++;
								loadTickets();
							}}>{$t('Next')}<i class="fa-solid fa-chevron-right ms-1"></i></Button
						>
					</div>
				</div>
			{/if}
		{/if}
	</section>
	{/if}
</div>
